import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Eye, Pencil, Power, Rocket, Zap } from 'lucide-react';
import {
    ENDED_BY_LABELS,
    JobDetail,
    JobEvent,
    JobStatusBadges,
    WakePlan,
    apiErrorMessage,
    blockedOnLabel,
    formatCredits,
    formatRelativeTime,
    jobSubject,
    personLabel,
} from '../services/servicesShared';
import { StewardNames, useOrgUsers } from '../services/StewardsSelect';
import { JobStreamMessage, useJobStream } from './useJobStream';
import JobStateCard from './JobStateCard';
import JobEventLog from './JobEventLog';
import JobSteering from './JobSteering';
import JobArtifacts from './JobArtifacts';
import JobWakes from './JobWakes';
import JobRoutines from './JobRoutines';
import { ApprovalDecideCard, GuidanceAnswerForm } from './JobQuestions';
import { ChangeOwnerDialog, StewardsDialog } from './JobPeopleDialogs';

const REFRESH_DEBOUNCE_MS = 600;

/** /jobs/:id — one owned job: state, action log, steering, routines, questions, approvals, artifacts, wakes. */
const JobPage: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { get, post } = useApi();
    const [job, setJob] = useState<JobDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [live, setLive] = useState(false);
    const [livePlan, setLivePlan] = useState<WakePlan | null>(null);
    const [focusWakeId, setFocusWakeId] = useState<string | null>(null);
    const [ownerOpen, setOwnerOpen] = useState(false);
    const [stewardsOpen, setStewardsOpen] = useState(false);
    const [turnOffOpen, setTurnOffOpen] = useState(false);
    const [goLiveOpen, setGoLiveOpen] = useState(false);
    const [goLiveContinue, setGoLiveContinue] = useState(false);
    const [offReason, setOffReason] = useState('');
    const [busy, setBusy] = useState<'status' | 'wake' | 'mode' | null>(null);
    const refreshTimer = useRef<number | null>(null);
    const orgUsers = useOrgUsers();

    const fetchJob = useCallback(
        async (silent = false) => {
            if (!id) return;
            try {
                if (!silent) setLoading(true);
                const response = await get(`/jobs/${id}`);
                setJob(response.data);
            } catch (error) {
                if (!silent) {
                    toast.error(apiErrorMessage(error, 'Job not found'));
                    navigate('/jobs');
                }
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [get, id, navigate],
    );

    useEffect(() => {
        void fetchJob();
    }, [fetchJob]);

    /** Advisory live payloads: re-read the view, coalesced. */
    const scheduleRefresh = useCallback(() => {
        if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
        refreshTimer.current = window.setTimeout(() => {
            refreshTimer.current = null;
            void fetchJob(true);
        }, REFRESH_DEBOUNCE_MS);
    }, [fetchJob]);

    useEffect(() => () => {
        if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
    }, []);

    const onStreamMessage = useCallback(
        (message: JobStreamMessage) => {
            setLive(true);
            switch (message.type) {
                case 'event':
                    setJob((prev) => {
                        if (!prev) return prev;
                        if (prev.events.some((event) => event.id === message.event.id)) return prev;
                        return { ...prev, events: [...prev.events, message.event as JobEvent] };
                    });
                    // Wake lifecycle rows change the wakes list and routines too.
                    if (String(message.event.kind).startsWith('wake_') || message.event.kind === 'routine_changed') scheduleRefresh();
                    break;
                case 'status':
                    setJob((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  status: message.status,
                                  blocked_on: (message.blocked_on as JobDetail['blocked_on']) ?? null,
                                  headline: message.headline ?? prev.headline,
                                  active_wake_id: message.active_wake_id,
                              }
                            : prev,
                    );
                    break;
                case 'plan':
                    setLivePlan(message.plan);
                    break;
                case 'state':
                    setJob((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  state: message.state ?? prev.state,
                                  details: message.details ?? prev.details,
                                  headline: message.headline ?? prev.headline,
                                  state_updated_at: new Date().toISOString(),
                              }
                            : prev,
                    );
                    break;
                case 'wake':
                    if (message.status !== 'running') setLivePlan(null);
                    scheduleRefresh();
                    break;
                default:
                    scheduleRefresh();
            }
        },
        [scheduleRefresh],
    );

    const lastSeq = job?.events.reduce((max, event) => Math.max(max, event.seq), 0) ?? 0;
    useJobStream(job?.id, {
        after: lastSeq,
        enabled: Boolean(job),
        onMessage: onStreamMessage,
        onReconnect: () => void fetchJob(true),
    });
    const applyJob = (next: Partial<JobDetail>) => setJob((prev) => (prev ? { ...prev, ...next } : prev));

    const setStatus = async (status: 'on' | 'off') => {
        if (!job) return;
        setBusy('status');
        try {
            const response = await post(`/jobs/${job.id}/status`, { status, reason: status === 'off' ? offReason.trim() || null : null });
            applyJob(response.data);
            toast.success(status === 'off' ? 'Job turned off' : 'Job turned on — a wake is queued');
            setTurnOffOpen(false);
            setOffReason('');
            scheduleRefresh();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not change the job status'));
        } finally {
            setBusy(null);
        }
    };

    const setMode = async (mode: 'live' | 'shadow', strategy: 'restart' | 'continue' = 'restart') => {
        if (!job) return;
        setBusy('mode');
        try {
            const response = await post(`/jobs/${job.id}/mode`, { mode, strategy });
            applyJob(response.data);
            toast.success(
                mode === 'shadow'
                    ? 'Job is in shadow mode — writes and messages are now recorded, not done'
                    : strategy === 'continue'
                      ? 'Job is live — a wake is queued to act on what it rehearsed'
                      : 'Job is live with a clean slate — a setup wake is queued',
            );
            setGoLiveOpen(false);
            setGoLiveContinue(false);
            scheduleRefresh();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not change the job mode'));
        } finally {
            setBusy(null);
        }
    };

    const wakeNow = async () => {
        if (!job) return;
        setBusy('wake');
        try {
            const response = await post(`/jobs/${job.id}/wake`, {});
            toast.success(response.data?.wake_id ? 'Wake queued' : 'A wake is already pending');
            scheduleRefresh();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not wake the job'));
        } finally {
            setBusy(null);
        }
    };

    if (loading || !job) {
        return (
            <div className="p-12 grow">
                <div className="animate-pulse space-y-4 max-w-4xl">
                    <div className="h-10 bg-muted rounded w-1/3" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                    <div className="h-40 bg-muted rounded" />
                    <div className="h-64 bg-muted rounded" />
                </div>
            </div>
        );
    }

    const stewardIds = job.steward_user_ids || job.effective_stewards || [];
    const serviceStewardIds = job.service?.steward_user_ids || [];
    const openQuestions = job.open_questions.filter((request) => request.status === 'open');
    const pendingApprovals = job.pending_approvals.filter((approval) => approval.status === 'pending');

    return (
        <div className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow">
            <div className="max-w-6xl">
                <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-4 w-4" /> Back
                </button>

                {/* Header */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-sm text-muted-foreground">
                            <Link to={`/services/${job.service_id}`} className="hover:underline">
                                {job.service_name || 'Service'}
                            </Link>
                            {job.version_number ? ` · v${job.version_number}` : ''}
                        </div>
                        <h1 className="text-3xl font-bold flex items-center gap-3 flex-wrap mt-1">
                            <span className="truncate">{jobSubject(job)}</span>
                            <JobStatusBadges job={job} />
                        </h1>
                        <div className="mt-2 text-sm text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                            <span>
                                Customer: <span className="text-foreground">{personLabel(job.owner)}</span>
                                <button type="button" className="ml-1 inline-flex align-middle text-muted-foreground hover:text-foreground" onClick={() => setOwnerOpen(true)} title="Change the customer contact">
                                    <Pencil className="h-3 w-3" />
                                </button>
                            </span>
                            <span>
                                Your team:{' '}
                                <span className="text-foreground">
                                    <StewardNames ids={stewardIds} byId={orgUsers.byId} />
                                </span>
                                {job.steward_user_ids == null ? <span className="text-xs text-muted-foreground"> (service default)</span> : null}
                                <button type="button" className="ml-1 inline-flex align-middle text-muted-foreground hover:text-foreground" onClick={() => setStewardsOpen(true)} title="Edit who on your team handles this job">
                                    <Pencil className="h-3 w-3" />
                                </button>
                            </span>
                            <span>
                                {job.wake_count ?? 0} wakes · {formatCredits(job.credits_used_period)} credits this period
                            </span>
                            {job.status === 'on' && job.next_wake_at && (
                                <span>
                                    Next wake {formatRelativeTime(job.next_wake_at)}
                                    {job.next_wake_reason ? ` (${job.next_wake_reason})` : ''}
                                </span>
                            )}
                        </div>
                        {job.status === 'off' && (
                            <p className="mt-2 text-sm text-muted-foreground">
                                Ended by {job.ended_by ? ENDED_BY_LABELS[job.ended_by] || job.ended_by : 'unknown'} {formatRelativeTime(job.ended_at)}
                                {job.ended_reason ? `: ${job.ended_reason}` : ''}. Turning it back on resumes the same job with its memory and workspace.
                            </p>
                        )}
                        {job.status === 'on' && job.blocked_on && (
                            <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
                                {blockedOnLabel(job.blocked_on)}
                                {job.blocked_detail?.question ? ` — ${job.blocked_detail.question}` : ''}
                                {job.blocked_detail?.tool ? ` — ${job.blocked_detail.tool}` : ''}
                                {job.blocked_detail?.reason ? ` — ${job.blocked_detail.reason}` : ''}
                            </p>
                        )}
                        {job.mode === 'shadow' && (
                            <p className="mt-2 text-sm text-purple-700 dark:text-purple-400">
                                Shadow mode: the job works as if live, but every write and customer message is recorded below as &ldquo;would have&rdquo; instead of
                                happening. The customer cannot see this job. Go live when you trust what it would do.
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {job.status === 'on' ? (
                            <>
                                {job.mode === 'shadow' ? (
                                    <Button onClick={() => setGoLiveOpen(true)} disabled={busy !== null} title="Writes and customer messages start happening">
                                        <Rocket className="h-4 w-4 mr-1" /> Go live
                                    </Button>
                                ) : (
                                    <Button variant="outline" onClick={() => setMode('shadow')} disabled={busy !== null} title="Keep working, but record writes and messages instead of doing them">
                                        <Eye className="h-4 w-4 mr-1" /> {busy === 'mode' ? 'Switching…' : 'Shadow'}
                                    </Button>
                                )}
                                <Button variant="outline" onClick={wakeNow} disabled={busy !== null} title="Queue a full wake now">
                                    <Zap className="h-4 w-4 mr-1" /> {busy === 'wake' ? 'Waking…' : 'Wake now'}
                                </Button>
                                <Button variant="outline" onClick={() => setTurnOffOpen(true)} disabled={busy !== null}>
                                    <Power className="h-4 w-4 mr-1" /> Turn off
                                </Button>
                            </>
                        ) : (
                            <Button onClick={() => setStatus('on')} disabled={busy !== null}>
                                <Power className="h-4 w-4 mr-1" /> {busy === 'status' ? 'Turning on…' : 'Turn on'}
                            </Button>
                        )}
                    </div>
                </div>

                <div className="mt-6">
                    <JobStateCard job={job} schema={job.state_schema || {}} />
                </div>

                {(openQuestions.length > 0 || pendingApprovals.length > 0) && (
                    <section className="mt-8 space-y-4">
                        {openQuestions.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-3">Open questions</h3>
                                <div className="space-y-3">
                                    {openQuestions.map((request) => (
                                        <GuidanceAnswerForm
                                            key={request.id}
                                            request={request}
                                            onAnswered={(updated) => {
                                                applyJob({ open_questions: job.open_questions.map((item) => (item.id === updated.id ? updated : item)) });
                                                scheduleRefresh();
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        {pendingApprovals.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-3">Pending approvals</h3>
                                <div className="space-y-3">
                                    {pendingApprovals.map((approval) => (
                                        <ApprovalDecideCard
                                            key={approval.id}
                                            approval={approval}
                                            onDecided={(updated) => {
                                                applyJob({ pending_approvals: job.pending_approvals.map((item) => (item.id === updated.id ? updated : item)) });
                                                scheduleRefresh();
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                )}

                <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
                    <div className="space-y-10 min-w-0">
                        <JobEventLog
                            events={job.events}
                            live={live}
                            restartedAt={job.restarted_at}
                            onWake={(wakeId) => {
                                setFocusWakeId(wakeId);
                                document.getElementById('job-wakes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                        />
                        <div id="job-wakes">
                            <JobWakes jobId={job.id} wakes={job.wakes} activeWakeId={job.active_wake_id} livePlan={livePlan} focusWakeId={focusWakeId} />
                        </div>
                    </div>
                    <div className="space-y-8 min-w-0">
                        <JobSteering
                            jobId={job.id}
                            messages={job.messages}
                            jobOn={job.status === 'on'}
                            onSent={(message) => applyJob({ messages: [...job.messages, message] })}
                        />
                        <JobRoutines
                            jobId={job.id}
                            routines={job.routines}
                            onChanged={(routine) => applyJob({ routines: job.routines.map((item) => (item.id === routine.id ? routine : item)) })}
                        />
                        <JobArtifacts jobId={job.id} artifacts={job.artifacts} />
                        {Object.keys(job.setup_answers || {}).length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-3">Setup answers</h3>
                                <dl className="rounded-lg border divide-y text-sm">
                                    {Object.entries(job.setup_answers).map(([key, value]) => (
                                        <div key={key} className="flex justify-between gap-3 px-3 py-1.5">
                                            <dt className="font-mono text-xs text-muted-foreground">{key}</dt>
                                            <dd className="text-right break-words">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ChangeOwnerDialog open={ownerOpen} onClose={() => setOwnerOpen(false)} job={job} onChanged={(next) => applyJob(next)} />
            <StewardsDialog
                open={stewardsOpen}
                onClose={() => setStewardsOpen(false)}
                job={job}
                serviceStewardIds={serviceStewardIds}
                users={orgUsers.users}
                usersLoading={orgUsers.loading}
                onChanged={(next) => applyJob(next)}
            />

            <Dialog open={turnOffOpen} onOpenChange={setTurnOffOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Turn this job off?</DialogTitle>
                        <DialogDescription>
                            It stops working for {jobSubject(job)}. Open questions and held approvals are withdrawn. State, memory and the
                            workspace are kept, so turning it back on resumes the same job.
                        </DialogDescription>
                    </DialogHeader>
                    <Input placeholder="Reason (optional, shown to the customer)" value={offReason} onChange={(event) => setOffReason(event.target.value)} />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTurnOffOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={() => setStatus('off')} disabled={busy !== null}>
                            {busy === 'status' ? 'Turning off…' : 'Turn off'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={goLiveOpen}
                onOpenChange={(open) => {
                    setGoLiveOpen(open);
                    if (!open) setGoLiveContinue(false);
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Go live?</DialogTitle>
                        <DialogDescription>
                            The job starts over for real: its writes to your product happen and its messages reach the customer. The rehearsal&rsquo;s
                            state, memory and workspace are set aside (the &ldquo;would have&rdquo; entries stay in the log for review), and a setup wake
                            is queued so it settles with the customer what the rehearsal could only assume.
                        </DialogDescription>
                    </DialogHeader>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <Checkbox checked={goLiveContinue} onCheckedChange={(value) => setGoLiveContinue(value === true)} className="mt-0.5" />
                        <span>
                            Continue from the rehearsal instead
                            <span className="block text-xs text-muted-foreground">
                                Keep its state, memory and workspace and queue a wake to carry out what it rehearsed. Only if you have reviewed the
                                rehearsal and want exactly that.
                            </span>
                        </span>
                    </label>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setGoLiveOpen(false)} disabled={busy !== null}>
                            Cancel
                        </Button>
                        <Button onClick={() => setMode('live', goLiveContinue ? 'continue' : 'restart')} disabled={busy !== null}>
                            {busy === 'mode' ? 'Switching…' : goLiveContinue ? 'Go live and continue' : 'Go live with a clean slate'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default JobPage;
